import React from 'react';
import { countryValueResolver } from 'helpers/location';
import env from 'config/environment';
import { i18n } from 'config/locales';
import { CommonOnUpdate } from 'types/props';
import { LocationModel } from 'forms/shared/types';
import {
  createLocationModelObjectFromProps,
  isSafeInternational,
  isSafePostOffice,
  isSafeUnitedStates,
} from 'helpers/locations';
import { COUNTRY_FOREIGN, COUNTRY_US } from 'constants/enums/locations';
import Branch from '../Branch';
import ValidationElement from '../ValidationElement';
import Street from '../Street';
import State from '../State';
import City from '../City';
import Country from '../Country';
import County from '../County';
import ZipCode from '../ZipCode';
import Layouts from './Layouts';
import { setAttribute } from '../shared/setAttribute';
import LocationValidator, { countryString } from '../../../validators/location';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';

/**
 * @deprecated
 */
const mappingWarning = (property) => {
  if (!env.IsTest()) {
    console.warn(`Could not map location property '${property}' in ToggleableLocation `);
  }
};

const branchValue = (addressObj: Partial<LocationModel>) => {
  if (isSafeUnitedStates(addressObj, false)) { return 'Yes'; }
  if (isSafeInternational(addressObj, false) || isSafePostOffice(addressObj, false)) { return 'No'; }

  return '';
};

interface Props extends Partial<LocationModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  domestic?: any
  // TODO: Update type to be specific to what it can accept
  domesticFields: string[]
  // TODO: Update type to be specific to what it can accept
  internationalFields: string[]
  attribute?: string
  streetLabel?: string
  street2Label?: string
  cityLabel?: string
  stateLabel?: string
  zipcodeLabel?: string
  countryLabel?: string
  countyLabel?: string
  countyPlaceholder?: string
  postOfficeStreetPlaceholder?: string
  postOfficeStateLabel?: string
  postOfficeZipcodeLabel?: string
  streetPlaceholder?: string
  cityPlaceholder?: string
  statePlaceholder?: string
  zipcodePlaceholder?: string
  countryPlaceholder?: string
  label?: string
  name?: string
  required?: boolean
}

interface TLState {
  uid: string
  suggestions: any[]
}

export default class ToggleableLocation extends ValidationElement<Props, TLState> {
  constructor(props) {
    super(props);

    this.state = {
      suggestions: [],
      uid: `${this.props.name}-${super.guid()}`,
    };
    this.errors = [];
  }

  update = (updateValues) => {
    if (this.props.onUpdate) {
      const nextValues = {
        street: this.props.street,
        city: this.props.city,
        zipcode: this.props.zipcode,
        state: this.props.state,
        country: this.props.country,
        countryComments: this.props.countryComments,
        county: this.props.county,
        domestic: this.props.domestic,
        domesticFields: this.props.domesticFields,
        internationalFields: this.props.internationalFields,
        ...updateValues,
      };
      this.props.onUpdate(nextValues);
    }
  };

  onAddressUpdate = (nextValue) => {
    const { name, value } = nextValue;

    this.update({
      [name]: value,
    });
  };

  updateCountry = (values) => {
    this.update({
      country: { value: values.value ?? '' },
      countryComments: values.comments,
    });
  };

  updateToggle = (option) => {
    // Set existing errors to null when toggling fields
    this.props.onError(
      option.value,
      this.errors.map((err) => ({
        code: err.code,
        valid: null,
        uid: err.uid,
      }))
    );

    switch (option.value) {
      case 'Yes':
        this.update({ country: { value: COUNTRY_US } });
        break;
      case 'No':
        this.update({ country: { value: COUNTRY_FOREIGN } });
        break;
      default:
    }
  };

  zipcodeInstate = () => {
    const validator = new LocationValidator(this.props);

    return validator.validZipcodeState();
  };

  onError = (value, arr) => {
    // eslint-disable-next-line no-param-reassign
    arr = arr.map((err) => ({
      code: `toggleablelocation.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    const requiredErr = arr.concat(
      this.constructor.errors.map((err) => ({
        code: `toggleablelocation.${err.code}`,
        valid: err.func(value, { ...this.props }),
        uid: this.state.uid,
      }))
    );

    this.storeErrors(requiredErr);
    this.props.onError(value, requiredErr);
    return arr;
  };

  storeErrors = (errors) => {
    const newErrors = [...errors];

    for (let i = 0; i < newErrors.length; i += 1) {
      const e = newErrors[i];
      const idx = this.errors.findIndex((x) => x.uid === e.uid && x.code === e.code);
      if (idx !== -1) {
        this.errors[idx] = { ...e };
      } else {
        this.errors.push({ ...e });
      }
    }
  };

  render() {
    const instateZipcode = this.zipcodeInstate();
    const locationObject = createLocationModelObjectFromProps(this.props);

    const requiredLayouts = this.props.layout === Layouts.IDENTIFICATION_BIRTH_PLACE
      || this.props.layout === Layouts.OFFENSE
      || this.props.layout === Layouts.BIRTHPLACE;

    const domesticFields = this.props.domesticFields.map((field) => {
      const key = `domestic-${field}`;
      switch (field) {
        case 'street':
          return (
            <Street
              attribute={`${this.props.attribute}.street`}
              name="street"
              className="mailing street"
              key={key}
              placeholder={this.props.streetPlaceholder}
              value={this.props.street}
              onUpdate={this.onAddressUpdate}
              onError={this.onError}
              onFocus={this.props.onFocus}
              required={this.props.required}
            />
          );
        case 'city':
          return (
            <City
              attribute={`${this.props.attribute}.city`}
              name="city"
              className="city"
              key={key}
              label={this.props.cityLabel}
              placeholder={this.props.cityPlaceholder}
              value={this.props.city}
              onUpdate={this.onAddressUpdate}
              onError={this.onError}
              onFocus={this.props.onFocus}
              required={
                requiredLayouts ? this.props.required && !this.props.county : this.props.required
              }
              requireCity={
                requiredLayouts ? this.props.required && !this.props.county : this.props.required
              }
            />
          );
        case 'county':
          return (
            <County
              attribute={`${this.props.attribute}.county`}
              name="county"
              key={key}
              label={this.props.countyLabel}
              value={this.props.county}
              className="county"
              placeholder={this.props.countyPlaceholder}
              maxlength="255"
              onUpdate={this.onAddressUpdate}
              onError={this.onError}
              onFocus={this.props.onFocus}
              required={
                requiredLayouts ? this.props.required && !this.props.city : this.props.required
              }
              requireCounty={
                requiredLayouts ? this.props.required && !this.props.city : this.props.required
              }
            />
          );
        case 'state':
          return (
            <State
              attribute={`${this.props.attribute}.state`}
              name="state"
              key={key}
              className="state"
              label={this.props.stateLabel}
              placeholder={this.props.statePlaceholder}
              value={this.props.state}
              includeStates="true"
              onUpdate={this.onAddressUpdate}
              onError={this.onError}
              onFocus={this.props.onFocus}
              onBlur={this.props.onBlur}
              required={this.props.required}
            />
          );
        case 'stateZipcode':
          return (
            <div className="state-zip-wrap" key={key}>
              <State
                attribute={`${this.props.attribute}.state`}
                name="state"
                className="state"
                label={this.props.stateLabel}
                placeholder={this.props.statePlaceholder}
                value={this.props.state}
                includeStates="true"
                onUpdate={this.onAddressUpdate}
                onError={this.onError}
                onFocus={this.props.onFocus}
                onBlur={this.props.onBlur}
                required={this.props.required}
              />
              <ZipCode
                attribute={`${this.props.attribute}.zipcode`}
                name="zipcode"
                key="us_zipcode"
                className="zipcode"
                label={this.props.zipcodeLabel}
                placeholder={this.props.zipcodePlaceholder}
                value={this.props.zipcode}
                status={instateZipcode}
                onUpdate={this.onAddressUpdate}
                onError={this.onError}
                onFocus={this.props.onFocus}
                required={this.props.required}
              />
            </div>
          );
        default:
          return null;
      }
    });

    const internationalFields = this.props.internationalFields.map((field) => {
      const key = `domestic-${field}`;
      switch (field) {
        case 'city':
          return (
            <City
              attribute={`${this.props.attribute}.city`}
              name="city"
              key={key}
              label={this.props.cityLabel}
              placeholder={this.props.cityPlaceholder}
              value={this.props.city}
              onUpdate={this.onAddressUpdate}
              onError={this.onError}
              onFocus={this.props.onFocus}
              required={this.props.required}
            />
          );
        case 'country':
          return (
            <Country
              isPartOfLocationModel
              attribute={`${this.props.attribute}.country`}
              name="country"
              key={key}
              label={this.props.countryLabel}
              {...countryValueResolver(this.props)}
              className="country"
              placeholder={this.props.countryPlaceholder}
              excludeUnitedStates="true"
              disabled={this.props.disabledCountry}
              onUpdate={this.updateCountry}
              onError={this.onError}
              onFocus={this.props.onFocus}
              required={this.props.required}
              updateCountryFocused={this.props.updateCountryFocused}
            />
          );
        default:
          return null;
      }
    });

    return (
      <div className="toggleable-location">
        <Branch
          attribute={setAttribute(this.props.attribute, 'layout')}
          label={this.props.label}
          name={this.props.name}
          noLabel={i18n.t('address.options.international.label')}
          onError={this.onError}
          onUpdate={this.updateToggle}
          required={this.props.required}
          yesLabel={i18n.t('address.options.us.label')}
          value={branchValue(locationObject)}
          disabled={this.props.disabled}
        />
        {isSafeUnitedStates(locationObject, false) && domesticFields}
        {isSafeInternational(locationObject, false) && internationalFields}
      </div>
    );
  }
}

ToggleableLocation.defaultProps = {
  country: { value: 'United States' },
  domesticFields: [],
  internationalFields: [],
  onError: (value, arr) => arr,
  required: false,
  scrollIntoView: false,
  updateCountryFocused: (focus) => { },
};

ToggleableLocation.errors = [
  {
    code: 'required',
    func: (value, props) => {
      if (!props.required) {
        return true;
      }

      // Organizing the validation tests in a structure
      const branchValidations = {
        Yes: {
          fields: (p) => p.domesticFields,
          city: (p) => (
            [Layouts.IDENTIFICATION_BIRTH_PLACE, Layouts.OFFENSE, Layouts.BIRTHPLACE].includes(p.layout)
              ? !!p.city || !!p.county
              : !!p.city),
          county: (p) => (
            [Layouts.IDENTIFICATION_BIRTH_PLACE, Layouts.OFFENSE, Layouts.BIRTHPLACE].includes(p.layout)
              ? !!p.city || !!p.county
              : !!p.county),
          state: (p) => !!p.state,
          stateZipcode: (p) => !!p.state && !!p.zipcode,
          country: (p) => !!countryString(p.country),
        },
        No: {
          fields: (p) => p.internationalFields,
          city: (p) => !!p.city,
          country: (p) => !!countryString(p.country),
        },
      };

      // Retrieve the branch value provided. If the value does match the
      // approved validation tree then return a negative response.
      const branch = branchValue(createLocationModelObjectFromProps(props));
      const validations = branchValidations[branch];
      if (!validations) {
        return false;
      }

      // Loop through all of the fields based on the branch and test for values.
      for (let i = 0; i < validations.fields(props).length; i += 1) {
        const f = validations.fields(props)[i];
        // Retrieve the test and if one is not found print a warning and continue
        const test = validations[f];
        if (!test) {
          mappingWarning(f);
          return false;
        }

        if (!test(props)) {
          return false;
        }
      }

      return true;
    },
  },
];
